<template>
  <div class="notice-page" v-loading="opening">
    <div class="page-heading"><h1>站内信通知</h1></div>
    <el-alert
      v-if="pageError"
      :title="pageError"
      type="error"
      show-icon
      :closable="false"
      class="page-error"
    />

    <section v-if="view === 'list'" class="notice-list">
      <div class="section-heading">
        <h2>发送任务</h2>
        <el-button
          v-if="canSave"
          type="primary"
          size="small"
          icon="el-icon-plus"
          @click="openEditor()"
          >新建通知</el-button
        >
      </div>
      <div class="list-panel">
        <div class="filters">
          <el-input
            v-model="keyword"
            size="medium"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索任务名称 / 通知标题"
            @keyup.enter.native="search"
          />
          <el-select v-model="status" size="medium"
            ><el-option label="全部状态" value=""/><el-option
              label="待发送"
              value="pending"/><el-option label="已发送" value="sent"
          /></el-select>
          <el-button size="small" @click="resetFilters">重置</el-button>
          <el-button type="primary" size="small" @click="search"
            >搜索</el-button
          >
        </div>
        <div class="list-summary">
          <span class="total">共 {{ total }} 条任务</span>
        </div>
        <el-table
          v-loading="loading"
          :data="list"
          empty-text="暂无发送任务"
          :header-cell-style="{
            background: '#f7f9fc',
            color: '#8190a4',
            fontWeight: 400
          }"
        >
          <el-table-column prop="id" label="ID" width="90" align="center" />
          <el-table-column
            prop="name"
            label="任务名称"
            min-width="200"
            align="center"
            header-align="center"
            class-name="task-name-column"
            label-class-name="task-name-column"
          >
            <template slot-scope="{ row }"
              ><button
                class="task-title"
                :title="row.name"
                @click="showDetail(row)"
              >
                {{ row.name }}
              </button></template
            >
          </el-table-column>
          <el-table-column
            prop="title"
            label="通知标题"
            min-width="240"
            align="center"
            header-align="center"
            show-overflow-tooltip
          >
            <template slot-scope="{ row }">{{ row.title || "—" }}</template>
          </el-table-column>
          <el-table-column
            prop="creator_name"
            label="创建用户"
            width="140"
            align="center"
            show-overflow-tooltip
          />
          <el-table-column label="通知样式" width="100"
            ><template slot-scope="{ row }"
              ><span class="muted">{{
                row.format === "card" ? "▧ 图文" : "≡ 文字"
              }}</span></template
            ></el-table-column
          >
          <el-table-column label="接收人数" width="120">
            <template slot="header" slot-scope="{}">
              <span class="recipient-count-heading">
                接收人数
                <el-tooltip placement="top">
                  <div slot="content" class="recipient-count-explanation">
                    按导入时有效且去重后的用户 ID 统计，同一用户只计 1
                    人，空行不计入。<br />
                    不存在、已删除或状态不正常的用户会自动排除；发送时发现账号失效，也会跳过并扣减人数。<br />
                    已发送记录保留计数，此处为接收总人数，不代表已发送或已读人数。
                  </div>
                  <button
                    type="button"
                    class="recipient-count-help"
                    aria-label="接收人数统计说明"
                  >
                    <i class="el-icon-warning-outline" aria-hidden="true" />
                  </button>
                </el-tooltip>
              </span>
            </template>
            <template slot-scope="{ row }"
              ><strong>{{ row.recipient_count }}</strong
              ><small class="muted"> 人</small></template
            ></el-table-column
          >
          <el-table-column label="发送状态" width="150"
            ><template slot-scope="{ row }"
              ><div class="status-main">
                <span
                  :class="[
                    'status-badge',
                    row.state === 'sent' ? 'sent' : 'pending'
                  ]"
                  >● {{ row.state === "sent" ? "已发送" : "待发送" }}</span
                >
                <el-tooltip
                  v-if="row.state !== 'sent'"
                  content="刷新发送状态"
                  placement="top"
                >
                  <el-button
                    type="text"
                    size="mini"
                    class="status-refresh"
                    icon="el-icon-refresh"
                    :loading="loading"
                    :disabled="loading"
                    :aria-label="'刷新任务 ' + row.id + ' 的发送状态'"
                    @click="loadList()"
                  />
                </el-tooltip>
              </div>
              <small v-if="row.state === 'queued'" class="progress-text"
                >{{ row.started_at ? "发送中" : "等待发送" }} ·
                {{ row.sent_count }}/{{ row.recipient_count }}</small
              ><small v-if="row.state === 'failed'" class="failure-text"
                >发送中断 · {{ row.sent_count }}/{{
                  row.recipient_count
                }}</small
              ></template
            ></el-table-column
          >
          <el-table-column label="创建时间" width="130"
            ><template slot-scope="{ row }"
              ><div class="time">
                {{ datePart(row.created_at)
                }}<small>{{ clockPart(row.created_at) }}</small>
              </div></template
            ></el-table-column
          >
          <el-table-column label="发送时间" width="130"
            ><template slot-scope="{ row }"
              ><div class="time">
                {{ datePart(row.sent_at)
                }}<small>{{ clockPart(row.sent_at) }}</small>
              </div></template
            ></el-table-column
          >
          <el-table-column label="操作" width="195" align="center"
            ><template slot-scope="{ row }"
              ><div class="actions">
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-view"
                  @click="showDetail(row)"
                  >查看</el-button
                ><el-button
                  v-if="canSave && row.state === 'draft'"
                  type="text"
                  size="mini"
                  icon="el-icon-edit"
                  @click="openEditor(row)"
                  >编辑</el-button
                ><el-button
                  v-if="canSend && ['draft', 'failed'].includes(row.state)"
                  type="text"
                  size="mini"
                  icon="el-icon-s-promotion"
                  @click="askSend(row)"
                  >{{ row.state === "failed" ? "继续发送" : "发送" }}</el-button
                >
              </div></template
            ></el-table-column
          >
        </el-table>
        <div class="pagination">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :page-sizes="[10, 20, 50]"
            :current-page="page"
            :page-size="pageSize"
            :total="total"
            @current-change="changePage"
            @size-change="changeSize"
          />
        </div>
      </div>
    </section>

    <section v-else class="editor">
      <div class="editor-toolbar">
        <el-button
          type="text"
          :disabled="busy"
          icon="el-icon-back"
          @click="returnToList"
          >返回任务列表</el-button
        ><strong>{{ form.id ? "编辑待发送任务" : "新建通知" }}</strong
        ><span class="muted">保存后进入待发送列表</span>
      </div>
      <div class="steps">
        <button
          :class="{ active: step === 1, done: step > 1 }"
          :disabled="busy"
          @click="step = 1"
        >
          <b>1</b><span>导入用户 ID<small>先确定发给谁</small></span></button
        ><i /><button
          :class="{ active: step === 2 }"
          :disabled="!form.recipientCount || busy"
          @click="step = 2"
        >
          <b>2</b><span>编辑通知<small>写内容，选去向</small></span>
        </button>
      </div>
      <div class="editor-layout">
        <div class="form-panel">
          <template v-if="step === 1">
            <div class="panel-heading">
              <h2>导入用户 ID</h2>
              <span>第 1 步 / 共 2 步</span>
            </div>
            <div class="panel-body">
              <div class="upload-zone">
                <i class="el-icon-upload2" />
                <h3>上传用户 ID 名单</h3>
                <p>
                  仅需「用户ID」一列 · 支持 .xlsx / .csv · 文件不超过 5
                  MB，大名单建议使用 CSV
                </p>
                <el-button
                  type="primary"
                  :loading="importing"
                  :disabled="busy"
                  @click="$refs.userFile.click()"
                  >选择文件</el-button
                ><el-button :disabled="busy" @click="downloadTemplate"
                  >下载导入模板</el-button
                >
              </div>
              <input
                ref="userFile"
                type="file"
                accept=".xlsx,.csv"
                hidden
                @change="importUsers"
              />
              <div v-if="form.recipientCount" class="imported-file">
                <i class="el-icon-document" />
                <div>
                  <strong>{{ form.fileName }}</strong>
                  <p>{{ form.recipientCount }} 位有效用户 · 已导入</p>
                </div>
                <i class="el-icon-circle-check" /><el-button
                  type="text"
                  :disabled="busy"
                  @click="removeImport"
                  >移除</el-button
                >
              </div>
              <div class="import-help">
                <strong>模板只需「用户ID」一列</strong>
                <p>每行填写一个用户 ID，相同 ID 自动合并，无效用户自动排除。</p>
              </div>
              <el-alert
                v-if="formError"
                :title="formError"
                type="error"
                :closable="false"
                show-icon
              />
            </div>
            <footer>
              <span class="muted">{{
                form.recipientCount
                  ? `已导入 ${form.recipientCount} 位有效用户`
                  : "请先上传用户 ID 名单"
              }}</span
              ><el-button
                type="primary"
                :disabled="!form.recipientCount || busy"
                @click="step = 2"
                >下一步：编辑通知 →</el-button
              >
            </footer>
          </template>
          <template v-else>
            <div class="panel-heading">
              <h2>编辑通知</h2>
              <span>第 2 步 / 共 2 步</span>
            </div>
            <el-form
              label-position="top"
              class="panel-body"
              :model="form"
              @submit.native.prevent="save"
            >
              <el-form-item label="任务名称" required
                ><el-input
                  v-model="form.name"
                  maxlength="60"
                  show-word-limit
                  placeholder="填写任务名称"
                /><small class="hint"
                  >仅后台可见，最多 60 字。</small
                ></el-form-item
              >
              <el-form-item label="通知样式"
                ><div class="segmented">
                  <button
                    type="button"
                    :class="{ selected: form.format === 'card' }"
                    @click="form.format = 'card'"
                  >
                    图文通知</button
                  ><button
                    type="button"
                    :class="{ selected: form.format === 'text' }"
                    @click="form.format = 'text'"
                  >
                    纯文字通知
                  </button>
                </div></el-form-item
              >
              <el-form-item
                v-if="form.format === 'card'"
                label="通知标题"
                required
                ><el-input
                  v-model="form.title"
                  maxlength="40"
                  show-word-limit
                  placeholder="用一句话说明这条通知"
              /></el-form-item>
              <el-form-item label="通知正文" required
                ><el-input
                  v-model="form.content"
                  type="textarea"
                  :rows="5"
                  :maxlength="form.format === 'card' ? 500 : 300"
                  show-word-limit
                  placeholder="填写通知正文"
              /></el-form-item>
              <template v-if="form.format === 'card'">
                <el-form-item label="通知配图" required
                  ><div class="cover-editor">
                    <div v-loading="uploading" class="cover-upload">
                      <button
                        type="button"
                        class="cover-select"
                        :class="{ 'has-image': form.image }"
                        :disabled="busy"
                        :aria-label="
                          form.image ? '重新上传通知配图' : '上传通知配图'
                        "
                        @click="$refs.coverFile.click()"
                      >
                        <img
                          v-if="form.image"
                          :src="imageUrl(form.image)"
                          alt="通知配图"
                        />
                        <span v-else class="cover-placeholder">
                          <i class="el-icon-plus" />
                          <small>上传配图</small>
                        </span>
                        <span
                          v-if="form.image"
                          class="cover-upload-action"
                          aria-hidden="true"
                          ><i class="el-icon-upload2"
                        /></span>
                      </button>
                      <button
                        v-if="form.image"
                        type="button"
                        class="cover-delete"
                        :disabled="busy"
                        aria-label="删除通知配图"
                        title="删除配图"
                        @click="removeImage"
                      >
                        <i class="el-icon-delete" aria-hidden="true" />
                      </button>
                    </div>
                    <p class="hint">
                      5 MB 内 PNG、JPG、WEBP，建议上传方形图片。
                    </p>
                  </div>
                  <input
                    ref="coverFile"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    @change="uploadImage"
                /></el-form-item>
                <div class="divider" />
                <el-form-item required>
                  <template slot="label">
                    <span>目标页面</span>
                    <el-tooltip
                      content="如需配置其它页面链接，请联系相关人员处理。"
                      placement="top"
                    >
                      <button
                        type="button"
                        class="target-picker-help"
                        aria-label="目标页面配置说明"
                      >
                        <i class="el-icon-warning-outline" aria-hidden="true" />
                      </button>
                    </el-tooltip>
                  </template>
                  <el-select
                    v-model="targetKind"
                    class="full-width"
                    @change="changeTarget"
                    ><el-option
                      v-for="item in targetKinds"
                      :key="item.value"
                      :value="item.value"
                      :label="item.label"/></el-select
                ></el-form-item>
                <el-form-item
                  v-if="!['member', 'pagc'].includes(targetKind)"
                  label="选择内容"
                  required
                  ><el-select
                    v-model="targetId"
                    filterable
                    remote
                    reserve-keyword
                    :remote-method="searchTargets"
                    :loading="targetLoading"
                    class="full-width"
                    placeholder="按名称或 ID 搜索"
                    @visible-change="onTargetOpen"
                    ><el-option
                      v-for="item in targets"
                      :key="item.id"
                      :value="item.id"
                      :label="
                        `${item.name || '未命名'} · ID ${item.id}`
                      "/></el-select
                ></el-form-item>
                <div v-if="targetKind === 'pagc'" class="two-col">
                  <el-form-item label="渠道 ID" required
                    ><el-input
                      v-model="channelId"
                      placeholder="填写渠道 ID"/></el-form-item
                  ><el-form-item label="优惠券 ID（选填）"
                    ><el-input v-model="couponId" placeholder="填写优惠券 ID"
                  /></el-form-item>
                </div>
              </template>
              <el-alert
                v-if="formError"
                :title="formError"
                type="error"
                :closable="false"
                show-icon
              />
            </el-form>
            <footer>
              <el-button :disabled="busy" @click="step = 1">← 上一步</el-button
              ><el-button
                type="primary"
                :loading="saving"
                :disabled="importing || uploading"
                @click="save"
                >保存并进入发送列表 →</el-button
              >
            </footer>
          </template>
        </div>
        <aside class="preview-panel">
          <h3>用户收到的通知</h3>
          <div class="phone-screen">
            <div class="phone-title">系统消息</div>
            <div class="preview-date">{{ previewTime }}</div>
            <message-preview :notice="form" :image-url="imageUrl(form.image)" />
          </div>
        </aside>
      </div>
    </section>

    <el-dialog
      :title="detail ? detail.name : '任务详情'"
      :visible.sync="detailVisible"
      width="640px"
      class="notice-detail"
      append-to-body
    >
      <template v-if="detail"
        ><div class="detail-meta">
          <div>
            <span>发送状态</span
            ><strong
              :class="[
                'status-badge',
                detail.state === 'sent' ? 'sent' : 'pending'
              ]"
              >{{ detail.state === "sent" ? "已发送" : "待发送" }}</strong
            >
          </div>
          <div>
            <span>接收人数</span
            ><strong>{{ detail.recipient_count }} 人</strong>
          </div>
          <div>
            <span>创建时间</span><strong>{{ detail.created_at }}</strong>
          </div>
          <div>
            <span>发送时间</span
            ><strong>{{ detail.sent_at || "尚未发送" }}</strong>
          </div>
        </div>
        <el-alert
          v-if="detail.state === 'failed'"
          :title="detail.last_error"
          type="error"
          :closable="false"
          show-icon/>
        <p v-if="['queued', 'failed'].includes(detail.state)" class="muted">
          {{ detail.state === "failed" ? "已暂停" : "发送处理中" }} · 已发送
          {{ detail.sent_count }}/{{ detail.recipient_count }} 人
        </p>
        <div class="detail-preview">
          <message-preview
            :notice="detail"
            :image-url="imageUrl(detail.image)"
          /></div
      ></template>
      <span slot="footer"
        ><el-button @click="detailVisible = false">关闭</el-button
        ><el-button
          v-if="detail && canSave && detail.state === 'draft'"
          @click="openEditor(detail)"
          >编辑</el-button
        ><el-button
          v-if="detail && canSend && ['draft', 'failed'].includes(detail.state)"
          type="primary"
          @click="askSend(detail)"
          >{{ detail.state === "failed" ? "继续发送" : "发送" }}</el-button
        ></span
      >
    </el-dialog>
    <el-dialog
      title="确认发送通知"
      :visible.sync="sendVisible"
      width="480px"
      append-to-body
      :close-on-click-modal="false"
      :show-close="!sending"
      :close-on-press-escape="!sending"
    >
      <template v-if="sendTask"
        ><p class="send-name">{{ sendTask.name }}</p>
        <div class="send-summary">
          <div>
            <span>本次接收人数</span
            ><strong
              >{{ sendTask.recipient_count - sendTask.sent_count
              }}<small> 人</small></strong
            >
          </div>
          <div>
            <span>通知样式</span
            ><strong>{{
              sendTask.format === "card" ? "图文通知" : "纯文字通知"
            }}</strong>
          </div>
        </div>
        <p class="muted">确认后将开始发送，任务内容将无法修改。</p>
        <el-alert
          v-if="sendError"
          :title="sendError"
          type="error"
          :closable="false"
      /></template>
      <span slot="footer"
        ><el-button :disabled="sending" @click="sendVisible = false"
          >取消</el-button
        ><el-button type="primary" :loading="sending" @click="confirmSend"
          >确认发送</el-button
        ></span
      >
    </el-dialog>
  </div>
</template>

<script>
import MessagePreview from "./MessagePreview.vue";

const targetKinds = [
  ["member", "会员中心"],
  ["activity", "活动详情"],
  ["community", "社群详情"],
  ["pagc", "PAGC 活动（渠道 / 领券）"],
  ["article", "资讯详情"],
  ["report", "报告详情"],
  ["features", "专题详情"],
  ["circle", "人脉详情"],
  ["enterprise", "企业详情"],
  ["cooperation", "合作详情"]
].map(([value, label]) => ({ value, label }));
const blankForm = () => ({
  id: 0,
  version: 0,
  name: "",
  format: "card",
  title: "",
  content: "",
  image: "",
  fileName: "",
  importId: 0,
  recipientCount: 0
});

export default {
  name: "MessageNotice",
  components: { MessagePreview },
  data() {
    return {
      view: "list",
      step: 1,
      form: blankForm(),
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      keyword: "",
      status: "",
      loading: false,
      opening: false,
      importing: false,
      uploading: false,
      saving: false,
      sending: false,
      pageError: "",
      formError: "",
      sendError: "",
      detail: null,
      detailVisible: false,
      sendTask: null,
      sendVisible: false,
      targetKinds,
      targetKind: "member",
      targetId: "",
      channelId: "",
      couponId: "",
      targets: [],
      targetLoading: false,
      listSequence: 0,
      targetSequence: 0,
      previewTime: new Date().toLocaleTimeString("zh-CN", {
        hour: "2-digit",
        minute: "2-digit"
      })
    };
  },
  computed: {
    canSave() {
      return this.$store.state.btn_if.includes("message_notice_save");
    },
    canSend() {
      return this.$store.state.btn_if.includes("message_notice_send");
    },
    busy() {
      return this.importing || this.uploading || this.saving;
    },
    selectedUrl() {
      const base = "https://m.yfchuhai.com";
      if (this.targetKind === "member") return base + "/member-center";
      if (this.targetKind === "community")
        return `${base}/community-detail?communityId=${this.targetId}&id=${this.targetId}`;
      if (this.targetKind === "pagc")
        return `${base}/pagc?channel=${this.channelId.trim()}${
          this.couponId.trim() ? "&scene=coupon_" + this.couponId.trim() : ""
        }`;
      return `${base}/${this.targetKind}/${this.targetId}.html`;
    }
  },
  created() {
    this.loadList();
  },
  beforeDestroy() {
    this.listSequence++;
    this.targetSequence++;
  },
  methods: {
    /** 调用本模块接口，业务错误交给页面对应位置展示。 */
    async api(action, data = {}, post = false) {
      const url = "/admin/MessageNotice/" + action;
      const response = post
        ? await this.$http.post(url, data, {
            timeout: action === "importUsers" ? 0 : 60000
          })
        : await this.$http.get(url, { params: data });
      // 全局响应拦截器在网络异常时可能返回空值，本模块提供可操作的提示。
      if (!response || !response.data)
        throw new Error("请求未完成，请刷新任务列表确认结果后重试");
      if (response.data.errorCode !== 200)
        throw new Error(response.data.message || "操作失败，请重试");
      return response.data.data;
    },
    imageUrl(path) {
      return path
        ? (this.$store.getters.imgCol || "").replace(/\/$/, "") + path
        : "";
    },
    datePart(value) {
      return value ? value.slice(0, 10) : "—";
    },
    clockPart(value) {
      return value ? value.slice(11, 16) : "";
    },
    /** 按当前筛选及页码加载列表；发送进度由运营手动刷新查看。 */
    async loadList() {
      const sequence = ++this.listSequence;
      this.loading = true;
      try {
        const data = await this.api("getList", {
          keyword: this.keyword.trim(),
          status: this.status,
          page: this.page,
          pageSize: this.pageSize
        });
        if (sequence !== this.listSequence) return;
        this.list = data.list;
        this.total = data.totalCount;
        this.pageError = "";
        if (this.detailVisible && this.detail) {
          const current = this.list.find(item => item.id === this.detail.id);
          if (current) this.detail = { ...this.detail, ...current };
        }
      } catch (error) {
        if (sequence === this.listSequence)
          this.pageError = error.message || "任务列表加载失败";
      } finally {
        if (sequence === this.listSequence) this.loading = false;
      }
    },
    search() {
      this.page = 1;
      this.loadList();
    },
    resetFilters() {
      this.keyword = "";
      this.status = "";
      this.search();
    },
    changePage(value) {
      this.page = value;
      this.loadList();
    },
    changeSize(value) {
      this.pageSize = value;
      this.search();
    },
    async openEditor(task) {
      if (!this.canSave || this.opening) return;
      this.opening = true;
      this.pageError = "";
      try {
        const item = task
          ? await this.api("getForEdit", { id: task.id })
          : null;
        this.form = item
          ? {
              ...blankForm(),
              ...item,
              fileName: item.file_name,
              recipientCount: item.recipient_count
            }
          : blankForm();
        this.restoreTarget(item ? item.url : "");
        this.formError = "";
        this.detailVisible = false;
        this.step = item ? 2 : 1;
        this.view = "edit";
        if (this.targetId) await this.searchTargets(this.targetId);
      } catch (error) {
        this.pageError = error.message;
      } finally {
        this.opening = false;
      }
    },
    returnToList() {
      this.view = "list";
      this.formError = "";
      this.loadList();
    },
    removeImport() {
      this.form.importId = 0;
      this.form.recipientCount = 0;
      this.form.fileName = "";
      this.formError = "";
    },
    downloadTemplate() {
      const blob = new Blob(["\ufeff用户ID\r\n"], {
        type: "text/csv;charset=utf-8"
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "接收名单_用户ID模板.csv";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    async importUsers(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file || this.busy) return;
      // 新名单导入成功后再替换，格式或账号校验失败时保留原有效名单。
      this.formError = "";
      this.importing = true;
      try {
        if (!/\.(xlsx|csv)$/i.test(file.name) || file.size > 5 * 1024 * 1024)
          throw new Error("请上传 5 MB 内的 .xlsx 或 UTF-8 .csv 文件");
        const body = new FormData();
        body.append("file", file);
        const result = await this.api("importUsers", body, true);
        this.form.importId = result.importId;
        this.form.recipientCount = result.count;
        this.form.fileName = result.fileName;
      } catch (error) {
        this.formError = error.message;
      } finally {
        this.importing = false;
      }
    },
    async uploadImage(event) {
      const file = event.target.files[0];
      event.target.value = "";
      if (!file || this.busy) return;
      this.uploading = true;
      this.formError = "";
      try {
        if (
          !["image/png", "image/jpeg", "image/webp"].includes(file.type) ||
          file.size > 5 * 1024 * 1024
        )
          throw new Error("请上传 5 MB 内的 PNG、JPG 或 WEBP 图片");
        const body = new FormData();
        body.append("file", file);
        const result = await this.api("uploadImage", body, true);
        this.form.image = result.url;
      } catch (error) {
        this.formError = error.message;
      } finally {
        this.uploading = false;
      }
    },
    /** 移除当前通知选中的配图，图文通知需要重新上传后才能保存。 */
    removeImage() {
      if (this.busy) return;
      this.form.image = "";
      this.formError = "";
    },
    /** 编辑时从已保存的受支持链接还原选择，避免移除粘贴入口后覆盖原目标。 */
    restoreTarget(url) {
      this.targetSequence++;
      this.targetLoading = false;
      this.targetKind = "member";
      this.targetId = this.channelId = this.couponId = "";
      this.targets = [];
      if (!url) return;
      const target = new URL(url);
      if (target.pathname === "/member-center") return;
      if (target.pathname === "/community-detail") {
        this.targetKind = "community";
        this.targetId = target.searchParams.get("communityId") || "";
      } else if (target.pathname === "/pagc") {
        this.targetKind = "pagc";
        this.channelId = target.searchParams.get("channel") || "";
        this.couponId = (target.searchParams.get("scene") || "").replace(
          /^coupon_/,
          ""
        );
      } else {
        const match = target.pathname.match(
          /^\/(activity|article|report|features|circle|enterprise|cooperation)\/([1-9][0-9]*)\.html$/
        );
        if (!match)
          throw new Error("该任务的目标页面暂不支持选择，请联系技术人员配置");
        this.targetKind = match[1];
        this.targetId = match[2];
      }
    },
    changeTarget() {
      this.targetId = "";
      this.targets = [];
      this.targetLoading = false;
      this.targetSequence++;
    },
    onTargetOpen(visible) {
      if (visible) this.searchTargets("");
    },
    async searchTargets(keyword) {
      const sequence = ++this.targetSequence;
      this.targetLoading = true;
      try {
        const data = await this.api("getTargets", {
          kind: this.targetKind,
          keyword
        });
        if (sequence === this.targetSequence) this.targets = data;
      } catch (error) {
        if (sequence === this.targetSequence) this.formError = error.message;
      } finally {
        if (sequence === this.targetSequence) this.targetLoading = false;
      }
    },
    /** 保存只创建待发送任务，必须回到列表另行确认发送。 */
    async save() {
      if (this.busy) return;
      this.formError = "";
      if (
        !this.form.recipientCount ||
        !this.form.name.trim() ||
        !this.form.content.trim()
      ) {
        this.formError = "请导入用户 ID，并填写任务名称和正文";
        return;
      }
      if (
        this.form.format === "card" &&
        (!this.form.title.trim() || !this.form.image)
      ) {
        this.formError = "请填写通知标题并上传配图";
        return;
      }
      if (
        this.form.content.length > (this.form.format === "card" ? 500 : 300)
      ) {
        this.formError = "图文通知正文最多 500 字，纯文字通知正文最多 300 字";
        return;
      }
      this.saving = true;
      try {
        const payload = {
          version: this.form.version,
          name: this.form.name,
          format: this.form.format,
          title: this.form.title,
          content: this.form.content,
          image: this.form.image,
          url: this.form.format === "card" ? this.selectedUrl : ""
        };
        if (this.form.id) payload.id = this.form.id;
        if (this.form.importId) payload.importId = this.form.importId;
        await this.api("save", payload, true);
        this.view = "list";
        this.page = 1;
        this.keyword = "";
        this.status = "";
        this.$message.success("已保存为待发送");
        await this.loadList();
      } catch (error) {
        this.formError = error.message;
      } finally {
        this.saving = false;
      }
    },
    async showDetail(task) {
      if (this.opening) return;
      this.opening = true;
      this.pageError = "";
      try {
        this.detail = await this.api("getById", { id: task.id });
        this.detailVisible = true;
      } catch (error) {
        this.pageError = error.message;
      } finally {
        this.opening = false;
      }
    },
    askSend(task) {
      this.sendTask = { ...task };
      this.sendError = "";
      this.sendVisible = true;
    },
    async confirmSend() {
      if (this.sending || !this.sendTask) return;
      this.sending = true;
      this.sendError = "";
      try {
        await this.api(
          "send",
          { id: this.sendTask.id, version: this.sendTask.version },
          true
        );
        this.sendVisible = false;
        this.detailVisible = false;
        this.$message.success("已提交发送，请在列表查看进度");
        await this.loadList();
      } catch (error) {
        this.sendError = error.message;
      } finally {
        this.sending = false;
      }
    }
  }
};
</script>

<style scoped>
.notice-page {
  background: #f5f7fb;
  min-height: calc(100vh - 130px);
  padding: 28px 30px 40px;
  color: #203754;
  font-size: 14px;
  text-align: left;
  box-sizing: border-box;
}
.notice-page h1 {
  font-size: 26px;
  margin: 0 0 30px;
  font-weight: 700;
}
.notice-page h2 {
  font-size: 20px;
  margin: 0;
  font-weight: 600;
}
.section-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}
.list-panel,
.form-panel {
  background: #fff;
  border: 1px solid #e6ecf5;
  border-radius: 10px;
  overflow: hidden;
}
.filters {
  padding: 20px 22px;
  display: flex;
  gap: 12px;
  align-items: center;
}
.filters > .el-input {
  width: 300px;
}
.filters > .el-select {
  width: 135px;
}
.filters .el-button + .el-button {
  margin-left: 0;
}
.list-summary {
  padding: 14px 22px;
  border-top: 1px solid #e6ecf5;
}
.total {
  color: #8797ad;
  white-space: nowrap;
}
.recipient-count-heading {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
.recipient-count-help,
.target-picker-help {
  padding: 0;
  border: 0;
  background: none;
  color: #909399;
  font-size: 14px;
  cursor: help;
}
.recipient-count-explanation {
  max-width: 300px;
  line-height: 1.7;
}
.task-title {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: #606266;
  font-weight: 400;
  line-height: 1.6;
  text-align: center;
  cursor: pointer;
  font-size: 13px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.task-title:hover {
  color: #409eff;
}
.muted,
.time {
  color: #8c9bb0;
  font-size: 12px;
}
.time small {
  display: block;
  margin-top: 3px;
}
.status-main {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.status-refresh {
  padding: 4px;
  font-size: 14px;
  color: #8797ad;
}
.status-refresh:hover,
.status-refresh:focus {
  color: #409eff;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  padding: 3px 9px;
  font-size: 12px;
  font-weight: 400;
  white-space: nowrap;
}
.pending {
  background: #fff5e4;
  color: #be8932;
}
.sent {
  background: #e9f7f1;
  color: #269d7a;
}
.progress-text,
.failure-text {
  display: block;
  font-size: 11px;
  margin-top: 4px;
  color: #899bb5;
}
.failure-text {
  color: #d78932;
}
.actions {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 12px;
}
.actions .el-button + .el-button {
  margin-left: 0;
}
.pagination {
  padding: 20px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #e9eef6;
}
.page-error {
  margin-bottom: 16px;
}
.editor-toolbar {
  display: flex;
  align-items: center;
  gap: 22px;
  margin-bottom: 25px;
}
.editor-toolbar > .muted {
  margin-left: auto;
}
.steps {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0 32px;
  gap: 24px;
}
.steps button {
  border: 0;
  background: none;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #8b9ab0;
  cursor: pointer;
  text-align: left;
  padding: 0;
}
.steps button:disabled {
  cursor: default;
}
.steps b {
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e7edf5;
  font-size: 16px;
}
.steps span {
  font-size: 16px;
  font-weight: 600;
}
.steps small {
  display: block;
  font-size: 12px;
  font-weight: 400;
  margin-top: 4px;
}
.steps .active {
  color: #3264f4;
}
.steps .active b {
  background: #3264f4;
  color: white;
}
.steps .done {
  color: #35a985;
}
.steps i {
  width: 100px;
  border-top: 1px solid #dce4ef;
}
.editor-layout {
  display: grid;
  grid-template-columns: minmax(400px, 1fr) 390px;
  gap: 24px;
  max-width: 1200px;
  margin: auto;
  align-items: start;
}
.panel-heading {
  padding: 23px 26px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e9eef6;
}
.panel-heading span {
  font-size: 12px;
  color: #93a0b3;
}
.panel-body {
  padding: 26px;
}
.upload-zone {
  border: 1px dashed #b9cae8;
  border-radius: 8px;
  text-align: center;
  background: #fafcff;
  padding: 40px 15px;
}
.upload-zone > i {
  font-size: 40px;
  color: #3264f4;
}
.upload-zone h3 {
  font-size: 17px;
  margin: 15px 0 10px;
}
.upload-zone p {
  font-size: 12px;
  color: #8a99b0;
  margin-bottom: 24px;
}
.imported-file {
  margin-top: 18px;
  padding: 17px;
  background: #f1f7ff;
  border: 1px solid #dae7fc;
  border-radius: 7px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.imported-file > i {
  font-size: 25px;
  color: #477de4;
}
.imported-file div {
  flex: 1;
  min-width: 0;
}
.imported-file strong {
  overflow-wrap: anywhere;
}
.imported-file p {
  font-size: 12px;
  color: #7f95b3;
  margin: 6px 0 0;
}
.imported-file .el-icon-circle-check {
  font-size: 20px;
  color: #35ae8a;
}
.import-help {
  margin: 22px 0;
  color: #7e90aa;
  font-size: 12px;
  line-height: 1.7;
}
.import-help strong {
  color: #536982;
}
.import-help p {
  margin: 5px 0;
}
footer {
  padding: 20px 26px;
  border-top: 1px solid #e9eef6;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.hint {
  display: block;
  font-size: 12px;
  color: #96a3b5;
  line-height: 1.6;
  margin: 8px 0 0;
}
.segmented {
  display: inline-flex;
  background: #f4f7fc;
  padding: 4px;
  border-radius: 6px;
  gap: 3px;
}
.segmented button {
  border: 0;
  background: none;
  border-radius: 4px;
  padding: 9px 16px;
  color: #7e8fa9;
  font-size: 13px;
  cursor: pointer;
}
.segmented .selected {
  background: white;
  color: #3264f4;
  box-shadow: 0 1px 5px #1b3f7210;
}
.full-width {
  width: 100%;
}
.target-picker-help {
  margin-left: 4px;
}
.cover-editor {
  display: flex;
  align-items: center;
  gap: 18px;
}
.cover-upload {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 96px;
}
.cover-select {
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 1px dashed #c0ccda;
  border-radius: 6px;
  background: #fafcff;
  cursor: pointer;
}
.cover-select.has-image {
  border: 0;
}
.cover-select:disabled,
.cover-delete:disabled {
  cursor: default;
}
.cover-select img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cover-placeholder {
  height: 100%;
  color: #909399;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.cover-placeholder i {
  font-size: 26px;
}
.cover-placeholder small {
  font-size: 12px;
}
.cover-upload-action {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 24px;
  opacity: 0;
  pointer-events: none;
}
.cover-delete {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  opacity: 0;
}
.cover-upload:hover .cover-upload-action,
.cover-upload:focus-within .cover-upload-action,
.cover-upload:hover .cover-delete,
.cover-upload:focus-within .cover-delete {
  opacity: 1;
}
.divider {
  border-top: 1px solid #edf0f6;
  margin: 25px 0;
}
.two-col {
  display: flex;
  gap: 18px;
}
.two-col > * {
  flex: 1;
  min-width: 0;
}
.preview-panel {
  border: 1px solid #e6ecf5;
  background: white;
  border-radius: 10px;
  padding: 20px 18px;
}
.preview-panel h3 {
  font-size: 15px;
  margin: 0 0 18px;
}
.phone-screen {
  background: #ededed;
  padding: 0 14px 26px;
  border-radius: 8px;
  min-height: 340px;
}
.phone-title {
  border-bottom: 1px solid #dfe2e6;
  text-align: center;
  padding: 16px;
  margin: 0 -14px;
  color: #24262b;
  font-size: 16px;
  font-weight: 600;
}
.preview-date {
  text-align: center;
  padding: 22px;
  color: #a3a3a3;
  font-size: 11px;
}
.detail-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin: 0 0 25px;
}
.detail-meta > div > span:first-child {
  display: block;
  color: #96a3b5;
  font-size: 12px;
  margin-bottom: 9px;
}
.detail-meta strong {
  font-size: 13px;
  font-weight: 500;
  color: #314862;
}
.detail-meta strong.pending {
  color: #be8932;
}
.detail-meta strong.sent {
  color: #269d7a;
}
.detail-preview {
  padding: 28px 22px;
  background: #ededed;
  border-radius: 7px;
  margin-top: 20px;
}
.send-name {
  font-size: 16px;
  color: #243d5a;
  overflow-wrap: anywhere;
}
.send-summary {
  display: flex;
  gap: 30px;
  background: #f7f9fc;
  border-radius: 7px;
  padding: 20px;
  margin: 20px 0;
}
.send-summary > div {
  flex: 1;
}
.send-summary span {
  display: block;
  color: #8c9bb0;
  font-size: 12px;
  margin-bottom: 12px;
}
.send-summary strong {
  font-size: 20px;
  color: #253c59;
}
.send-summary small {
  font-size: 12px;
  font-weight: 400;
}
.notice-page /deep/ .el-table td {
  padding: 20px 0;
  border-color: #edf1f7;
}
.notice-page /deep/ .el-table .cell {
  padding-left: 22px;
  padding-right: 16px;
}
.notice-page /deep/ .el-table .task-name-column .cell {
  text-align: center;
}
.notice-page /deep/ .el-table th {
  padding: 12px 0;
  font-size: 12px;
}
.notice-page /deep/ .el-table::before {
  display: none;
}
.notice-page /deep/ .el-form-item__label {
  line-height: 23px;
  padding-bottom: 8px;
  color: #3a506c;
}
.notice-page /deep/ .el-form-item {
  margin-bottom: 24px;
}
.notice-page /deep/ .el-button--primary {
  background: #3264f4;
  border-color: #3264f4;
}
.notice-page /deep/ .el-button--primary.is-disabled {
  background: #a5baf7;
  border-color: #a5baf7;
}
.notice-list /deep/ .el-button--primary {
  background: #409eff;
  border-color: #409eff;
}
.notice-list /deep/ .el-button--primary:hover,
.notice-list /deep/ .el-button--primary:focus {
  background: #66b1ff;
  border-color: #66b1ff;
}
@media (max-width: 1200px) {
  .notice-page {
    padding: 22px 18px;
  }
  .editor-layout {
    grid-template-columns: minmax(350px, 1fr) 340px;
    gap: 16px;
  }
  .filters {
    flex-wrap: wrap;
  }
  .filters > .el-input {
    width: 260px;
  }
}
@media (max-width: 950px) {
  .editor-layout {
    grid-template-columns: minmax(0, 1fr);
  }
  .preview-panel {
    max-width: 390px;
    width: 100%;
    box-sizing: border-box;
    justify-self: center;
  }
  .editor-toolbar {
    flex-wrap: wrap;
  }
  .editor-toolbar > .muted {
    margin-left: 0;
  }
  .steps {
    gap: 12px;
  }
  .steps i {
    width: 40px;
  }
}
</style>
